import { Box, Stack, Typography } from '@mui/material';

import { severityColor } from '@/lib/alert-severity';
import { PROGRAM_LABEL } from '@/lib/program-labels';

export type AlertType = 'unused' | 'drop' | 'voice-unused';

export interface AlertBadgeProps {
  type: AlertType;
  /** 색은 종류가 아니라 심각도를 따른다 */
  severity: string;
  detail: string;
}

/**
 * 접두어에 프로그램을 밝힌다 — 두 앱이 붙은 뒤로는 '미사용'만으로 무엇을 안 썼는지
 * 알 수 없다. 목록 폭 때문에 사내 약어(BGT / VW)를 쓴다. 전체 이름은 상단 배너·
 * 상세 경보 카드에서 그대로 보인다.
 */
const PREFIX: Record<AlertType, string> = {
  unused: `${PROGRAM_LABEL.bgt.short} 미사용`,
  drop: `${PROGRAM_LABEL.bgt.short} 급락`,
  'voice-unused': `${PROGRAM_LABEL.voice.short} 미사용`,
};

/**
 * 의뢰서 4-3 / 5-1 경보 표시.
 *
 * 배경을 채운 배지가 아니라 **작은 색 점 + 텍스트**로 그린다.
 *
 * 시설 대부분이 경보 대상인 상황이 흔하다(운영 데이터 기준 입소자 30명 중 28명).
 * 행마다 분홍 배경 배지가 깔리면 표 전체가 붉어져서, 정작 급한 대상이 묻힌다.
 * 색을 점에만 쓰면 행이 많아도 조용하고 심각도는 그대로 읽힌다.
 *
 * 색은 경보 종류가 아니라 **심각도**를 따른다. 종류로 정하면 일반 경보인
 * 보이스웰니스 미사용이 긴급과 같은 빨강으로 보인다.
 */
export function AlertBadge({ type, severity, detail }: AlertBadgeProps) {
  const tone = severityColor(severity);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      component="span"
      data-testid={`alert-badge-${type}`}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: tone.dot,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" component="span" color={tone.text} fontWeight={600} noWrap>
        {PREFIX[type]} {detail}
      </Typography>
    </Stack>
  );
}
